import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Plus, RefreshCw, PlugZap, Clock, PlaneTakeoff, Pause, Play, Ban, Trash2, MoreHorizontal, Pencil, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import AIAgentWizard from "@/components/AIAgentWizard";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ENCRYPTIONS = [
  { value: "ssl", label: "SSL/TLS" },
  { value: "starttls", label: "STARTTLS" },
  { value: "none", label: "None" },
];

const EMPTY = {
  company_id: "",
  department_ids: [],
  display_name: "",
  email_address: "",
  username: "",
  password: "",
  imap_username: "",
  imap_password: "",
  smtp_username: "",
  smtp_password: "",
  imap_host: "imap.hostinger.com",
  imap_port: 993,
  imap_encryption: "ssl",
  smtp_host: "smtp.hostinger.com",
  smtp_port: 465,
  smtp_encryption: "ssl",
  reply_to: "",
  default_sender_name: "",
  signature: "",
  ai_enabled: true,
  auto_send_enabled: false,
};

export default function EmailAccounts() {
  const { activeCompanyId } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoReply, setAutoReply] = useState(null); // { acct, start, end, msg, enabled, days, hStart, hEnd, tz }
  const [vacation, setVacation] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [wizardAcct, setWizardAcct] = useState(null);

  const setStatus = async (acct, patch) => {
    try {
      await api.patch(`/email-accounts/${acct.id}`, patch);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/email-accounts/${pendingDelete.id}`);
      toast.success(`Deleted ${pendingDelete.email_address}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [a, c, d] = await Promise.all([
      api.get("/email-accounts", { params }),
      api.get("/companies"),
      api.get("/departments", { params }),
    ]);
    setAccounts(a.data); setCompanies(c.data); setDepts(d.data);
    if (!form.company_id) setForm((f) => ({ ...f, company_id: activeCompanyId || c.data[0]?.id || "" }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const testUnsaved = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data } = await api.post("/email-accounts/test-credentials", form);
      setTestResult(data);
      if (data.imap_ok && data.smtp_ok) toast.success("IMAP and SMTP connections OK");
      else toast.error("Connection failed — check details");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/email-accounts", form);
      toast.success(`Account ${data.email_address} added`);
      setOpen(false); setForm(EMPTY); setTestResult(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
    setBusy(false);
  };

  const runTest = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/email-accounts/${id}/test`);
      if (data.imap_ok && data.smtp_ok) toast.success("Both connections OK");
      else toast.error(`IMAP: ${data.imap_ok ? "OK" : data.imap_info} · SMTP: ${data.smtp_ok ? "OK" : data.smtp_info}`);
      load();
    } catch (e) { toast.error("Test failed"); }
    setBusy(false);
  };

  const syncNow = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/email-accounts/${id}/sync`);
      toast.success(`Sync done: ${data.new} new, ${data.duplicates} duplicates`);
      load();
    } catch (e) { toast.error("Sync failed"); }
    setBusy(false);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="email-accounts-page">
      <PageHeader
        title="Email accounts"
        subtitle="Connect IMAP/SMTP mailboxes to companies and departments. Credentials are stored encrypted."
        actions={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTestResult(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-account-button"><Plus className="h-4 w-4 mr-1.5" /> Add mailbox</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Connect mailbox</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <F label="Company">
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="acct-company-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Department">
                    <Select
                      value={form.department_ids[0] || ""}
                      onValueChange={(v) => setForm({ ...form, department_ids: v ? [v] : [] })}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="acct-dept-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>
                        {depts.filter((d) => d.company_id === form.company_id).map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </F>
                </div>
                <F label="Display name">
                  <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required data-testid="acct-display-input" />
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Email address">
                    <Input type="email" value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value })} required data-testid="acct-email-input" />
                  </F>
                  <F label="Username override">
                    <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="defaults to email" data-testid="acct-username-input" />
                  </F>
                </div>
                <F label="Shared password (used when IMAP/SMTP passwords are empty)">
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="acct-password-input" placeholder="Optional if you provide separate IMAP/SMTP credentials" />
                </F>

                <div className="rounded-xl border border-border p-3 bg-muted/20 space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">IMAP credentials (optional — overrides shared)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={form.imap_username} onChange={(e) => setForm({ ...form, imap_username: e.target.value })} placeholder="IMAP username" data-testid="acct-imap-username-input" />
                    <Input type="password" value={form.imap_password} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} placeholder="IMAP password" data-testid="acct-imap-password-input" />
                  </div>
                </div>
                <div className="rounded-xl border border-border p-3 bg-muted/20 space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">SMTP credentials (optional — overrides shared)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={form.smtp_username} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} placeholder="SMTP username" data-testid="acct-smtp-username-input" />
                    <Input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} placeholder="SMTP password" data-testid="acct-smtp-password-input" />
                  </div>
                  <div className="text-[10px] text-muted-foreground">Use a different SMTP host (e.g. Amazon SES, Postmark, Mailgun) with your IMAP mailbox.</div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <F label="IMAP host">
                    <Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} data-testid="acct-imap-host-input" />
                  </F>
                  <F label="Port">
                    <Input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })} data-testid="acct-imap-port-input" />
                  </F>
                  <F label="Security">
                    <Select value={form.imap_encryption} onValueChange={(v) => setForm({ ...form, imap_encryption: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="acct-imap-enc-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ENCRYPTIONS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <F label="SMTP host">
                    <Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} data-testid="acct-smtp-host-input" />
                  </F>
                  <F label="Port">
                    <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} data-testid="acct-smtp-port-input" />
                  </F>
                  <F label="Security">
                    <Select value={form.smtp_encryption} onValueChange={(v) => setForm({ ...form, smtp_encryption: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="acct-smtp-enc-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ENCRYPTIONS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Sender name">
                    <Input value={form.default_sender_name} onChange={(e) => setForm({ ...form, default_sender_name: e.target.value })} data-testid="acct-sender-input" />
                  </F>
                  <F label="Reply-to">
                    <Input value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} data-testid="acct-replyto-input" />
                  </F>
                </div>
                <F label="Signature">
                  <Textarea value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} data-testid="acct-signature-input" />
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <ToggleRow label="AI enabled" checked={form.ai_enabled} onChange={(v) => setForm({ ...form, ai_enabled: v })} testid="acct-ai-toggle" />
                  <ToggleRow label="Auto-send" checked={form.auto_send_enabled} onChange={(v) => setForm({ ...form, auto_send_enabled: v })} testid="acct-autosend-toggle" />
                </div>

                {testResult && (
                  <div className="rounded-sm border border-border p-3 text-xs font-mono space-y-1">
                    <div>IMAP: <span className={testResult.imap_ok ? "text-emerald-deep" : "text-destructive"}>{testResult.imap_ok ? "OK" : testResult.imap_info}</span></div>
                    <div>SMTP: <span className={testResult.smtp_ok ? "text-emerald-deep" : "text-destructive"}>{testResult.smtp_ok ? "OK" : testResult.smtp_info}</span></div>
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={testUnsaved} disabled={busy} className="rounded-sm" data-testid="acct-test-button">
                    <PlugZap className="h-4 w-4 mr-1.5" /> Test connection
                  </Button>
                  <Button type="submit" disabled={busy} className="rounded-sm" data-testid="acct-save-button">Save account</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-8">
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="tbl w-full" data-testid="email-accounts-table">
            <thead>
              <tr>
                <th>Mailbox</th><th>Company</th><th>IMAP</th><th>SMTP</th><th>Last sync</th><th>AI</th><th></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const c = companies.find((x) => x.id === a.company_id);
                return (
                  <tr key={a.id} data-testid={`account-row-${a.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-sm bg-primary/10 text-primary grid place-items-center">
                          <Mail className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-1.5">
                            {a.display_name}
                            {(a.status && a.status !== "active") && (
                              <Badge className={`text-[9px] rounded-sm font-mono uppercase ${a.status === "paused" ? "bg-sun/25 text-yellow-700" : "bg-crimson/15 text-crimson"}`} data-testid={`account-status-${a.id}`}>
                                {a.status}
                              </Badge>
                            )}
                          </div>
                          <div className="text-[11px] font-mono text-muted-foreground">{a.email_address}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-muted-foreground">{c?.trading_name || c?.legal_name}</td>
                    <td><StatusBadge s={a.imap_status} /></td>
                    <td><StatusBadge s={a.smtp_status} /></td>
                    <td className="font-mono text-xs text-muted-foreground">{a.last_sync_ok?.slice(0, 16).replace("T", " ") || "—"}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <AIStatusPill acct={a} depts={depts} onQuickToggle={(patch) => setStatus(a, patch)} />
                      </div>
                    </td>
                    <td className="text-right space-x-1">
                      <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => runTest(a.id)} disabled={busy} data-testid={`test-${a.id}`}>
                        <PlugZap className="h-3.5 w-3.5 mr-1" /> Test
                      </Button>
                      <Button size="sm" className="rounded-sm text-xs" onClick={() => syncNow(a.id)} disabled={busy} data-testid={`sync-${a.id}`}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync
                      </Button>
                      <Button size="sm" className="rounded-sm text-xs bg-terracotta hover:bg-terracotta/90 text-white" onClick={() => setWizardAcct(a)} disabled={busy} data-testid={`ai-agent-${a.id}`}>
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> AI Agent
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm text-xs border-sun/40 text-yellow-700" onClick={() => setVacation({ acct: a, days: 7, subject: "I'm currently out of office", message: "Thanks for your email — I'm away and will respond when I return.", body: "Hi {{full_name}},\n\nA quick note — I'll be away for the next few days and may respond slower than usual. For anything urgent, our team will be happy to help.\n\nThanks,", top: 50, send_announcement: true })} disabled={busy} data-testid={`vacation-${a.id}`}>
                        <PlaneTakeoff className="h-3.5 w-3.5 mr-1" /> Vacation Kit
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="rounded-sm text-xs h-8 w-8 p-0" data-testid={`manage-${a.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl w-52">
                          <DropdownMenuItem onClick={() => setAutoReply({
                            acct: a,
                            enabled: !!a.ai_auto_reply_enabled,
                            start: a.ai_auto_reply_start ? a.ai_auto_reply_start.slice(0, 16) : "",
                            end: a.ai_auto_reply_end ? a.ai_auto_reply_end.slice(0, 16) : "",
                            msg: a.ai_auto_reply_message || "",
                            days: a.ai_auto_reply_days || [],
                            hStart: a.ai_auto_reply_hour_start ?? 0,
                            hEnd: a.ai_auto_reply_hour_end ?? 24,
                            tz: a.ai_auto_reply_timezone || "UTC",
                          })} data-testid={`autoreply-${a.id}`}>
                            <Clock className="h-3.5 w-3.5 mr-2" /> Advanced auto-reply schedule
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setEditing(a)} data-testid={`edit-account-${a.id}`}>
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit mailbox
                          </DropdownMenuItem>
                          {(a.status || "active") !== "active" && (
                            <DropdownMenuItem onClick={() => setStatus(a, { status: "active", active: true })} data-testid={`activate-account-${a.id}`}>
                              <Play className="h-3.5 w-3.5 mr-2 text-emerald-brand" /> Resume
                            </DropdownMenuItem>
                          )}
                          {(a.status || "active") !== "paused" && (
                            <DropdownMenuItem onClick={() => setStatus(a, { status: "paused" })} data-testid={`pause-account-${a.id}`}>
                              <Pause className="h-3.5 w-3.5 mr-2 text-yellow-600" /> Pause sync
                            </DropdownMenuItem>
                          )}
                          {(a.status || "active") !== "suspended" && (
                            <DropdownMenuItem onClick={() => setStatus(a, { status: "suspended", active: false })} data-testid={`suspend-account-${a.id}`}>
                              <Ban className="h-3.5 w-3.5 mr-2 text-crimson" /> Suspend
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setPendingDelete(a)} className="text-crimson focus:text-crimson" data-testid={`delete-account-${a.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete mailbox
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {accounts.length === 0 && <tr><td colSpan="7" className="text-center text-muted-foreground py-8">No mailboxes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Agent wizard */}
      <AIAgentWizard
        account={wizardAcct}
        open={!!wizardAcct}
        onClose={() => setWizardAcct(null)}
        onSaved={() => { setWizardAcct(null); load(); }}
      />

      {/* Auto-reply schedule modal */}
      <Dialog open={!!autoReply} onOpenChange={(o) => !o && setAutoReply(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="autoreply-modal">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">AI auto-reply schedule</DialogTitle></DialogHeader>
          {autoReply && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Configure an out-of-office / campaign window for <span className="font-mono text-foreground">{autoReply.acct.email_address}</span>.
                Auto-reply fires only when: (1) inside the absolute UTC window and (2) inside the recurring day-of-week + local-hour window.
              </p>
              <ToggleRow label="Enable auto-reply" checked={autoReply.enabled} onChange={(v) => setAutoReply({ ...autoReply, enabled: v })} testid="autoreply-enable" />
              <div className="grid grid-cols-2 gap-3">
                <F label="Start (UTC)">
                  <Input type="datetime-local" value={autoReply.start} onChange={(e) => setAutoReply({ ...autoReply, start: e.target.value })} className="rounded-xl" data-testid="autoreply-start" />
                </F>
                <F label="End (UTC)">
                  <Input type="datetime-local" value={autoReply.end} onChange={(e) => setAutoReply({ ...autoReply, end: e.target.value })} className="rounded-xl" data-testid="autoreply-end" />
                </F>
              </div>

              <div className="rounded-xl border border-border p-3 bg-muted/20 space-y-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recurring window</div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Active days</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="autoreply-days">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
                      const on = (autoReply.days || []).length === 0 || autoReply.days.includes(i);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => {
                            const cur = new Set(autoReply.days || []);
                            if (cur.has(i)) cur.delete(i); else cur.add(i);
                            setAutoReply({ ...autoReply, days: Array.from(cur).sort() });
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-mono border transition-colors ${on ? "bg-emerald-brand/12 text-emerald-brand border-emerald-brand/30" : "border-border text-muted-foreground hover:bg-muted"}`}
                          data-testid={`autoreply-day-${i}`}
                        >{d}</button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">Empty selection = every day.</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <F label="Hour start">
                    <Input type="number" min={0} max={23} value={autoReply.hStart} onChange={(e) => setAutoReply({ ...autoReply, hStart: Number(e.target.value) })} className="rounded-xl" data-testid="autoreply-hour-start" />
                  </F>
                  <F label="Hour end">
                    <Input type="number" min={1} max={24} value={autoReply.hEnd} onChange={(e) => setAutoReply({ ...autoReply, hEnd: Number(e.target.value) })} className="rounded-xl" data-testid="autoreply-hour-end" />
                  </F>
                  <F label="Timezone">
                    <Input value={autoReply.tz} onChange={(e) => setAutoReply({ ...autoReply, tz: e.target.value })} placeholder="e.g. America/New_York" className="rounded-xl" data-testid="autoreply-tz" />
                  </F>
                </div>
              </div>

              <F label="Custom message (optional — AI generates when empty)">
                <Textarea rows={5} value={autoReply.msg} onChange={(e) => setAutoReply({ ...autoReply, msg: e.target.value })} className="rounded-xl" data-testid="autoreply-message" />
              </F>
              <div className="flex justify-end">
                <Button className="rounded-xl" onClick={async () => {
                  try {
                    await api.patch(`/email-accounts/${autoReply.acct.id}`, {
                      ai_auto_reply_enabled: autoReply.enabled,
                      ai_auto_reply_start: autoReply.start ? new Date(autoReply.start).toISOString() : null,
                      ai_auto_reply_end: autoReply.end ? new Date(autoReply.end).toISOString() : null,
                      ai_auto_reply_message: autoReply.msg,
                      ai_auto_reply_days: autoReply.days,
                      ai_auto_reply_hour_start: autoReply.hStart,
                      ai_auto_reply_hour_end: autoReply.hEnd,
                      ai_auto_reply_timezone: autoReply.tz,
                    });
                    toast.success("Auto-reply schedule saved");
                    setAutoReply(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }} data-testid="autoreply-save">Save schedule</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vacation Kit modal */}
      <Dialog open={!!vacation} onOpenChange={(o) => !o && setVacation(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vacation-modal">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2"><PlaneTakeoff className="h-5 w-5 text-yellow-700" /> Vacation Kit</DialogTitle></DialogHeader>
          {vacation && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                One-click activator: enables auto-reply for {vacation.days} day(s) on <span className="font-mono">{vacation.acct.email_address}</span>
                {vacation.send_announcement && <> and sends an announcement to your top {vacation.top} recent contacts</>}.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <F label="Days away">
                  <Input type="number" min={1} max={90} value={vacation.days} onChange={(e) => setVacation({ ...vacation, days: Number(e.target.value) })} className="rounded-xl" data-testid="vacation-days" />
                </F>
                <F label="Top contacts to notify">
                  <Input type="number" min={0} max={500} value={vacation.top} onChange={(e) => setVacation({ ...vacation, top: Number(e.target.value) })} className="rounded-xl" data-testid="vacation-top" />
                </F>
              </div>
              <ToggleRow label="Send announcement email" checked={vacation.send_announcement} onChange={(v) => setVacation({ ...vacation, send_announcement: v })} testid="vacation-send-announcement" />
              <F label="Auto-reply message"><Textarea rows={3} value={vacation.message} onChange={(e) => setVacation({ ...vacation, message: e.target.value })} className="rounded-xl" data-testid="vacation-autoreply" /></F>
              <F label="Announcement subject"><Input value={vacation.subject} onChange={(e) => setVacation({ ...vacation, subject: e.target.value })} className="rounded-xl" data-testid="vacation-subject" /></F>
              <F label="Announcement body ({{full_name}}, {{email}})">
                <Textarea rows={5} value={vacation.body} onChange={(e) => setVacation({ ...vacation, body: e.target.value })} className="rounded-xl" data-testid="vacation-body" />
              </F>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-xl" onClick={async () => {
                  try {
                    await api.post("/vacation-kit/deactivate", { account_id: vacation.acct.id });
                    toast.success("Vacation Kit deactivated");
                    setVacation(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }} data-testid="vacation-deactivate">Deactivate</Button>
                <Button className="rounded-xl shadow-md shadow-primary/20" onClick={async () => {
                  try {
                    const { data } = await api.post("/vacation-kit/activate", {
                      account_id: vacation.acct.id,
                      days: vacation.days,
                      auto_reply_message: vacation.message,
                      announcement_subject: vacation.subject,
                      announcement_body: vacation.body,
                      top_contacts: vacation.top,
                      send_announcement: vacation.send_announcement,
                    });
                    toast.success(`Kit activated · ${data.recipients} announcements queued`);
                    setVacation(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }} data-testid="vacation-activate">Activate Kit</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit mailbox dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-account-modal">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Edit mailbox</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              const patch = {
                display_name: editing.display_name,
                default_sender_name: editing.default_sender_name || "",
                reply_to: editing.reply_to || "",
                imap_host: editing.imap_host, imap_port: Number(editing.imap_port), imap_encryption: editing.imap_encryption,
                smtp_host: editing.smtp_host, smtp_port: Number(editing.smtp_port), smtp_encryption: editing.smtp_encryption,
                ai_enabled: editing.ai_enabled, auto_send_enabled: editing.auto_send_enabled,
                daily_send_limit: Number(editing.daily_send_limit), hourly_send_limit: Number(editing.hourly_send_limit),
              };
              if (editing._password) patch.password = editing._password;
              try {
                await api.patch(`/email-accounts/${editing.id}`, patch);
                toast.success("Mailbox updated");
                setEditing(null); load();
              } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
            }} className="space-y-3">
              <F label="Display name"><Input value={editing.display_name} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} data-testid="edit-display-name" /></F>
              <F label="Default sender name"><Input value={editing.default_sender_name || ""} onChange={(e) => setEditing({ ...editing, default_sender_name: e.target.value })} data-testid="edit-sender-name" /></F>
              <F label="Reply-to"><Input type="email" value={editing.reply_to || ""} onChange={(e) => setEditing({ ...editing, reply_to: e.target.value })} data-testid="edit-reply-to" /></F>
              <div className="grid grid-cols-3 gap-3">
                <F label="IMAP host"><Input value={editing.imap_host} onChange={(e) => setEditing({ ...editing, imap_host: e.target.value })} /></F>
                <F label="IMAP port"><Input type="number" value={editing.imap_port} onChange={(e) => setEditing({ ...editing, imap_port: e.target.value })} /></F>
                <F label="Encryption">
                  <Select value={editing.imap_encryption} onValueChange={(v) => setEditing({ ...editing, imap_encryption: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ENCRYPTIONS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <F label="SMTP host"><Input value={editing.smtp_host} onChange={(e) => setEditing({ ...editing, smtp_host: e.target.value })} /></F>
                <F label="SMTP port"><Input type="number" value={editing.smtp_port} onChange={(e) => setEditing({ ...editing, smtp_port: e.target.value })} /></F>
                <F label="Encryption">
                  <Select value={editing.smtp_encryption} onValueChange={(v) => setEditing({ ...editing, smtp_encryption: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ENCRYPTIONS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
              <F label="New password (leave blank to keep)"><Input type="password" value={editing._password || ""} onChange={(e) => setEditing({ ...editing, _password: e.target.value })} data-testid="edit-password" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Daily limit"><Input type="number" value={editing.daily_send_limit} onChange={(e) => setEditing({ ...editing, daily_send_limit: e.target.value })} /></F>
                <F label="Hourly limit"><Input type="number" value={editing.hourly_send_limit} onChange={(e) => setEditing({ ...editing, hourly_send_limit: e.target.value })} /></F>
              </div>
              <ToggleRow label="AI enabled" checked={editing.ai_enabled} onChange={(v) => setEditing({ ...editing, ai_enabled: v })} testid="edit-ai-enabled" />
              <ToggleRow label="Auto-send AI drafts" checked={editing.auto_send_enabled} onChange={(v) => setEditing({ ...editing, auto_send_enabled: v })} testid="edit-auto-send" />
              <DialogFooter><Button type="submit" className="rounded-xl" data-testid="edit-account-save">Save changes</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-account-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Delete this mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-mono">{pendingDelete?.email_address}</span> from Relay CRM.
              Existing synced messages remain but the mailbox can no longer send or receive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-crimson hover:bg-crimson/90" onClick={doDelete}>Delete mailbox</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    ok: { cls: "bg-emerald-deep/15 text-emerald-deep", label: "OK" },
    error: { cls: "bg-destructive/15 text-destructive", label: "ERROR" },
    unknown: { cls: "bg-muted text-muted-foreground", label: "—" },
  };
  const m = map[s] || map.unknown;
  return <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${m.cls}`}>{m.label}</span>;
}

function AIStatusPill({ acct, depts, onQuickToggle }) {
  const dept = depts?.find((d) => d.id === (acct.department_ids || [])[0]);
  const on = acct.ai_enabled !== false;
  const autoReply = !!acct.ai_auto_reply_enabled;
  const days = acct.ai_auto_reply_days || [];
  const scheduleLabel = autoReply
    ? (days.length === 0 ? "every day" : (days.length === 7 ? "every day" : `${days.length}d`)) +
      ` · ${String(acct.ai_auto_reply_hour_start ?? 0).padStart(2, "0")}–${String(acct.ai_auto_reply_hour_end ?? 24).padStart(2, "0")}`
    : "";

  return (
    <div className="flex flex-col gap-1" data-testid={`ai-status-pill-${acct.id}`}>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onQuickToggle({ ai_enabled: !on })}
          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border transition-colors ${on ? "bg-emerald-brand/12 text-emerald-brand border-emerald-brand/30" : "bg-muted text-muted-foreground border-border"}`}
          data-testid={`quick-ai-toggle-${acct.id}`}
          title={on ? "Turn AI off" : "Turn AI on"}
        >
          {on ? "AI ON" : "AI OFF"}
        </button>
        {acct.auto_send_enabled && (
          <span className="text-[10px] font-mono uppercase tracking-widest bg-terracotta text-white px-1.5 py-0.5 rounded-sm">auto</span>
        )}
        {autoReply && (
          <span className="text-[10px] font-mono uppercase tracking-widest bg-sky-500/15 text-sky-700 px-1.5 py-0.5 rounded-sm">reply</span>
        )}
      </div>
      <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[180px]" title={`${dept?.name || "no dept"} · ${scheduleLabel || "no schedule"}`}>
        {dept?.name || "no dept"}{scheduleLabel ? ` · ${scheduleLabel}` : ""}
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-sm border border-border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
